import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {nanoid} from 'nanoid'
import {useCallback, useEffect, useRef} from 'react'
import {isClient} from '@/utils/common'
import {TYPE_APPLICATION_JS} from '~/app/constants'

const generateScriptIdentifier = async (script) => {
    if (script.src) {
        return `src:${script.src}`
    }

    if (script.content) {
        const preview = script.content.slice(0, 50)
        // Use Web Crypto API to generate SHA-256 hash
        const encoder = new TextEncoder()
        const data = encoder.encode(script.content)
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', data)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
        const shortHash = hashHex.slice(0, 8) // Shorten hash for readability
        return `content:${preview}|hash:${shortHash}`
    }

    // Added to handle loading of scripts without ids
    return nanoid()
}

const addScript = (scriptSrc) => {
    const {
        id = generateScriptIdentifier(scriptSrc),
        src,
        content,
        loadingStrategy,
        onload,
        onerror,
        attributes = {},
        shouldLoad = true,
        type = TYPE_APPLICATION_JS,
        fetchpriority = 'auto'
    } = scriptSrc

    return new Promise((resolve, reject) => {
        if (!shouldLoad || document.head.querySelector(`script[id="${id}"]`)) {
            return resolve()
        }

        const isLoadingStrategyConfigured =
            loadingStrategy === 'async' || loadingStrategy === 'defer'
        const script = document.createElement('script')
        script.id = id
        script.type = type
        script.fetchpriority = fetchpriority

        if (src) {
            script.src = src
        } else if (content) {
            script.innerHTML = content
        }

        if (Object.entries(attributes).length) {
            Object.entries(attributes).forEach(([key, value]) => {
                script.setAttribute(key, value)
            })
        }

        script.onerror = (error) => {
            logger.error('Script loading error', {
                namespace: 'hooks.useScript.addScript',
                additionalProperties: {error, id}
            })
            onerror?.(error)
            reject(error)
        }

        if (isLoadingStrategyConfigured) {
            script.async = loadingStrategy === 'async'
            script.defer = loadingStrategy === 'defer'
            script.onload = onload

            document.head.appendChild(script)
            resolve()
        } else {
            script.onload = () => {
                onload?.()
                resolve()
            }
            document.head.appendChild(script)
        }
    })
}

export const useScript = (config = {}) => {
    const {scripts = []} = config
    const configRef = useRef(config)
    const isInitialRender = useRef(true)
    const loadScripts = useCallback(async () => {
        const currentConfig = configRef.current
        currentConfig.beforeLoad?.()

        for (const scriptConfig of currentConfig.scripts) {
            try {
                await addScript(scriptConfig)
            } catch (error) {
                const {src, id} = scriptConfig
                logger.error('Failed to load script', {
                    additionalParameters: {
                        scriptId: id,
                        src: src,
                        error
                    }
                })
            }
        }
    }, [])

    useEffect(() => {
        let intervalId
        let hasLoaded = false

        const checkAndLoad = () => {
            if (isClient && window.dataLayer && !hasLoaded) {
                hasLoaded = true
                loadScripts()
                clearInterval(intervalId)
            }
        }

        if (isInitialRender.current) {
            configRef.current = config
            checkAndLoad()
            if (!hasLoaded) {
                intervalId = setInterval(checkAndLoad, 100)
            }
        }

        isInitialRender.current = false

        return () => {
            if (intervalId) clearInterval(intervalId)
        }
    }, [scripts, loadScripts])
}
